<template>
  <div class="ach-card-wrap">
    <!-- 加载中状态 - 仅在初始数据加载时显示 -->
    <div v-if="!dataLoaded" class="loading-container">
      <van-loading type="spinner" color="#333" />
      <div class="loading-text">
        {{ $t("common.loadingCommonText") || "加载中..." }}
      </div>
    </div>

    <!-- 银行卡列表 - 仅在数据加载完成且有卡并且未显示Adyen组件时显示 -->
    <div v-if="dataLoaded && cardList.length > 0 && !showAchPayment">
      <AchCardListComponent
        :cardList="cardList"
        :initialSelectedCardId="selectedCardId"
        @card-selected="onCardSelected"
        @add-new-card="selectNewCard"
        @delete-card="onDeleteCard"
        ref="cardListComponent"
        :primaryColor="primaryColor"
      />
    </div>

    <!-- Adyen支付组件 -->
    <div v-if="dataLoaded && showAchPayment">
      <!-- 返回卡支付按钮 - 右上角 -->
      <div class="backButton" v-if="cardList.length > 0" @click="goBack()">
        <div
          class="backIcon"
          :style="{ backgroundColor: primaryColor, borderRadius: '50%' }"
        >
          <span>&lt;</span>
        </div>
        <span class="backText">{{ $t("common.returnCardList") }}</span>
      </div>

      <!-- 引入adyen支付组件 -->
      <component
        v-if="achPaymentComponentLoaded"
        :is="$options.components.AchPaymentComponent"
        :environment="environment"
        :clientKey="clientKey"
        @payment-error="handlePaymentError"
        @payment-submit="handlePaymentSubmit"
        @payment-change="handlePaymentChange"
        @payment-completed="handlePaymentCompleted"
        ref="achPaymentComponent"
      />
      <!-- 已移除Adyen组件的loading状态，实现直接展示 -->
    </div>

    <!-- 底部支付按钮 - 仅在数据加载完成后显示 -->
    <div class="payBtnBox" v-if="dataLoaded">
      <div class="agreement-group">
        <van-checkbox
          v-model="userAgreed"
          @change="handleAgreement"
          shape="square"
          :color="primaryColor"
        >
        </van-checkbox>
        <div class="agreement-text">
          <!-- <span>通过ACH直接付款的授权</span> -->
          <agreement-popup
            :primaryColor="primaryColor"
            @agreement-confirmed="handleAgreementConfirmed"
            :isPhone="true"
            :amount="totalFee"
            @popup-closed="handlePopupClosed"
          />
        </div>
      </div>

      <div
        class="payBtn"
        @click="confirmPayment()"
        :class="{
          disabled: !canPay
        }"
        :style="
          canPay
            ? { background: primaryColor, borderRadius: btnRadiusDp }
            : { backgroundColor: '#ccc', borderRadius: btnRadiusDp }
        "
      >
        {{ $t("common.payConfirm") }}
        <span style="margin-left:0.08rem">{{ currencySymbol }}</span>
        {{ totalFee }}
      </div>
    </div>
  </div>
</template>

<script>
import "@/style/moblie/reset.css";
import { Toast } from "vant";
import AgreementPopup from "@/components/AgreementPopup.vue";
// 懒加载的组件不在这里直接引入
// import AchPaymentComponent from "@/components/AchPaymentComponent.vue";
import AchCardListComponent from "@/components/AchCardListComponent.vue";
import api from "@/api/api";
import utils from "@/utils/index";
import indexMixin from "../../mixins/index";

export default {
  name: "achCardList",
  components: {
    AgreementPopup,
    // 懒加载组件
    AchPaymentComponent: () => import("@/components/AchPaymentComponent.vue"),
    AchCardListComponent
  },
  mixins: [indexMixin],
  data() {
    return {
      cardList: [],
      selectedCardId: "",
      bankAccountToken: "",
      loading: false,
      showAchPayment: false,
      achPaymentComponentLoaded: false,
      dataLoaded: false,
      userAgreed: true,
      currencySymbol: this.$route.query.currencySymbol || "",
      environment: this.$route.query.environment || "test",
      clientKey: this.$route.query.clientKey || "",
      isFormValid: true,
      extendParam: "",
      totalFee: this.$route.query.totalFee || "",
      bankAccountListToken: "",
      checkoutType: this.$route.query.checkoutType || 0,
      primaryColor: this.$route.query.primaryColor
        ? `#${this.$route.query.primaryColor}`
        : "#ffaa0a",
      btnRadiusDp: this.$route.query.btnRadiusDp || "0.44rem",
      returnUrl: this.$route.query.returnUrl || "",
      isQueryingOrder: false,
      interval: null,
      // 预先获取的url参数，避免频繁调用
      cachedParams: {}
    };
  },
  computed: {
    // 计算是否能够支付的条件
    canPay() {
      return (
        this.userAgreed &&
        ((this.selectedCardId && this.selectedCardId !== "new-card") ||
          (this.showAchPayment && this.isFormValid))
      );
    }
  },
  methods: {
    // 获取卡列表数据
    async fetchCardList() {
      if (this.loading) return;
      this.loading = true;

      try {
        api.apiMethod.getCardList
          .post(
            {
              businessCode: this.cachedParams.businessCode,
              merchantPayNo: this.cachedParams.merchantPayNo
            },
            {
              checkoutToken: this.cachedParams.checkoutToken,
              bankAccountListToken: this.cachedParams.bankAccountListToken
            }
          )
          .then(res => {
            // 标记数据已加载完成
            this.dataLoaded = true;

            if (res.data.code === 10000) {
              this.cardList = res.data.data;

              // 只有当接口请求返回的cardList确实为空数组时，才展示Adyen组件
              if (Array.isArray(this.cardList) && this.cardList.length === 0) {
                this.showAchPayment = true;
                this.selectedCardId = "new-card"; // 设置为新卡支付模式
                // 直接设置组件已加载，不再使用延迟加载
                this.achPaymentComponentLoaded = true;
              } else if (
                Array.isArray(this.cardList) &&
                this.cardList.length > 0
              ) {
                // 如果有卡列表数据，默认选中第一张卡
                this.initialSelectedCardId = this.cardList[0].id;
                this.selectedCardId = this.cardList[0].bankAccountToken;
                this.bankAccountToken = this.cardList[0].bankAccountToken;
                this.showAchPayment = false;
              }

              this.reportLog({
                eventId: "GETCARD_H5",
                msg: ""
              });
            } else {
              // 接口调用失败但仍然标记为数据已加载
              this.showAchPayment = true;
              this.selectedCardId = "new-card"; // 设置为新卡支付模式
              // 直接设置组件已加载，不再使用延迟加载
              this.achPaymentComponentLoaded = true;
              this.reportLog({
                eventId: "GETCARD_ERROR_H5",
                msg: res.data.msg || ""
              });
            }
          })
          .finally(() => {
            this.loading = false;
          });
      } catch (error) {
        console.error("Error fetching card list:", error);
        // 错误情况下也标记数据已加载
        this.dataLoaded = true;
        this.reportLog({
          eventId: "GETCARD_ERROR_H5",
          msg: error.message
        });
        this.showAchPayment = true;
        this.selectedCardId = "new-card"; // 设置为新卡支付模式
        // 直接设置组件已加载，不再使用延迟加载
        this.achPaymentComponentLoaded = true;
        this.loading = false;
      }
    },

    // 加载支付组件
    loadAchPaymentComponent() {
      // 改为直接设置组件已加载，不再使用延迟
      this.achPaymentComponentLoaded = true;
    },

    // 处理选择卡片
    onSelectCard(cardId) {
      this.selectedCardId = cardId.bankAccountToken;
      this.bankAccountToken = cardId.bankAccountToken;
      this.showAchPayment = false;
    },

    // 处理选择添加新银行卡
    selectNewCard() {
      this.reportLog({
        eventId: "SELECTNEWCARD_H5",
        msg: ""
      });

      this.selectedCardId = "new-card";
      this.showAchPayment = true;
      // 直接设置组件已加载，跳过loading状态
      this.achPaymentComponentLoaded = true;
    },

    // 处理删除卡片
    onDeleteCard(cardId) {
      this.reportLog({
        eventId: "DELETECARD_COMFIRM_H5",
        msg: ""
      });

      // 调用删除接口
      api.apiMethod.deleteCard
        .post(
          {
            businessCode: this.cachedParams.businessCode,
            merchantPayNo: this.cachedParams.merchantPayNo
          },
          {
            checkoutToken: this.cachedParams.checkoutToken,
            bankAccountToken: cardId.bankAccountToken
          }
        )
        .then(res => {
          if (res.data.code === 10000) {
            // subCode 200 表示删除成功
            if (res.data.subCode === "200") {
              Toast.success({
                message: this.$t("common.deleteCardSuccess"),
                className: "custom-toast-success"
              });
              this.selectedCardId = "";
              this.bankAccountToken = "";
              this.fetchCardList();
            } else {
              Toast.fail({
                message: this.$t("common.deleteCardError"),
                className: "custom-toast-error"
              });
            }
          } else {
            this.reportLog({
              eventId: "DELETECARD_ERROR_H5",
              msg: ""
            });
            Toast.fail({
              message: this.$t("common.deleteCardError"),
              className: "custom-toast-error"
            });
          }
        });
    },

    // 缓存URL参数
    cacheUrlParams() {
      this.cachedParams = {
        checkoutToken: utils.getUrlSearch("checkoutToken"),
        bankAccountListToken: utils.getUrlSearch("bankAccountListToken"),
        businessCode: utils.getUrlSearch("businessCode"),
        merchantPayNo: utils.getUrlSearch("merchantPayNo"),
        payProduct: utils.getUrlSearch("payProduct") || "61"
      };
    },

    // 确认支付
    async confirmPayment() {
      if (!this.canPay) return;

      this.reportLog({
        eventId: "CONFIRMPAYMENT_H5",
        msg: ""
      });

      // 开启loading
      Toast.loading({
        message: this.$t("common.payingText"),
        forbidClick: true,
        duration: 0,
        className: "custom-toast-loading"
      });

      try {
        // 构建支付参数
        const params = this.buildPaymentParams();

        // 如果是新卡支付，先进行giact验证
        if (this.isNewCardPayment()) {
          console.log("开始 giact 验证");
          const verifyResult = await this.verifyGiact(params);
          console.log("giact 验证结果:", verifyResult);
          if (!verifyResult) {
            console.log("giact 验证失败，停止支付流程");
            this.handlePaymentError(
              this.$t("common.giactVerifyFailed") || "giact验证失败"
            );
            return;
          }
          console.log("giact 验证成功，继续支付流程");
        }

        // 调用支付接口
        console.log("准备调用支付接口，参数:", params);
        const response = await api.apiMethod.goPay.post(params, {
          checkoutToken: this.cachedParams.checkoutToken
        });

        if (response.data.code === 10000) {
          this.handlePaymentSuccess();
        } else {
          this.handlePaymentError(this.$t("common.payError"));
        }
      } catch (error) {
        this.handlePaymentError(error.message || "支付处理出错，请稍后再试");
        console.error("Error processing payment:", error);
      }
    },

    // 构建支付参数
    buildPaymentParams() {
      const params = {
        checkoutType: Number(this.checkoutType),
        businessCode: this.cachedParams.businessCode,
        payProduct: this.cachedParams.payProduct,
        merchantPayNo: this.cachedParams.merchantPayNo,
        extendParam: {}
      };

      if (this.selectedCardId && this.selectedCardId !== "new-card") {
        // 使用已有卡片支付
        params.extendParam.bankAccountToken = this.bankAccountToken;
        params.extendParam = JSON.stringify(params.extendParam);
      } else if (this.showAchPayment) {
        // 使用新卡支付
        params.extendParam = JSON.stringify(this.extendParam || {});
      }

      return params;
    },

    // 判断是否为新卡支付
    isNewCardPayment() {
      const result = this.showAchPayment && this.selectedCardId === "new-card";
      console.log("判断是否为新卡支付:", {
        showAchPayment: this.showAchPayment,
        selectedCardId: this.selectedCardId,
        result
      });
      return result;
    },

    // giact验证
    async verifyGiact(params) {
      try {
        const response = await api.apiMethod.verifyGiact.post(
          {
            merchantPayNo: this.cachedParams.merchantPayNo,
            payProduct: this.cachedParams.payProduct,
            extendParam: utils.buildAccountVerifyExtendParam(
              params.extendParam
            )
          },
          {
            checkoutToken: this.cachedParams.checkoutToken
          }
        );

        console.log("giact 验证结果:", response);

        // 检查验证结果，支持字符串 "true" 和布尔值 true
        const verificationResult = response.data?.data?.verificationResult;
        const isValid =
          verificationResult === "true" || verificationResult === true;

        console.log("验证结果解析:", { verificationResult, isValid });

        if (isValid) {
          this.reportLog({
            eventId: "GIACT_VERIFY_SUCCESS_H5",
            msg: "giact验证成功"
          });
          return true;
        } else {
          this.reportLog({
            eventId: "GIACT_VERIFY_FAILED_H5",
            msg: `giact验证失败: ${verificationResult}`
          });
          return false;
        }
      } catch (error) {
        console.error("Giact verification error:", error);
        this.reportLog({
          eventId: "GIACT_VERIFY_ERROR_H5",
          msg: error.message || "API request failed"
        });
        return false;
      }
    },

    // 处理支付成功
    handlePaymentSuccess() {
      Toast.clear();
      this.reportLog({
        eventId: "PAYSUCCESS_H5",
        msg: ""
      });
      this.queryOrder(true, this.$route.query.app);
    },

    // 处理支付错误
    handlePaymentError(message) {
      Toast.clear();
      this.reportLog({
        eventId: "PAYERROR_H5",
        msg: message || ""
      });
      Toast.fail({
        message: message || this.$t("common.payError"),
        className: "custom-toast-error"
      });
    },

    // 处理协议勾选
    handleAgreement(value) {
      // 只有当value是布尔值时(用户实际点击)才记录日志，防止因程序修改userAgreed导致的额外事件记录
      if (typeof value === "boolean") {
        // 如果是true，记录同意事件
        if (value) {
          this.reportLog({
            eventId: "AGREEMENT",
            msg: ""
          });
        } else {
          // 否则记录取消同意事件
          this.reportLog({
            eventId: "AGREEMENTCANCEL",
            msg: ""
          });
        }
      }
    },

    /**
     * 处理授权协议确认事件
     */
    handleAgreementConfirmed() {
      // 自动勾选协议
      this.userAgreed = true;

      // 添加日志记录
      this.reportLog({
        eventId: "AGREEMENT_CONFIRMED_H5",
        msg: ""
      });
    },

    /**
     * 处理弹窗关闭事件
     */
    handlePopupClosed(data) {
      // 如果是通过点击"我已阅读并同意"按钮关闭的，则设置userAgreed为true
      // 否则（通过点击关闭按钮关闭）保持userAgreed原值不变
      if (data && data.hasAgreed) {
        // 如果之前userAgreed是false，现在应该设为true
        if (!this.userAgreed) {
          this.userAgreed = true;
        }
      }
      // 如果不是通过同意按钮关闭的，保持userAgreed原值不变
    },

    /**
     * 处理卡片选择
     */
    onCardSelected(card) {
      this.selectedCardId = card.bankAccountToken;
      this.bankAccountToken = card.bankAccountToken;
      this.showAchPayment = false;
    },

    /**
     * 处理支付完成事件
     */
    handlePaymentCompleted() {
      // 这里可以添加支付完成后的逻辑，如跳转到成功页面
      const successMsg = this.$t("common.paymentSuccess") || "支付成功";

      // 如果是英文，则分行处理
      if (this.$i18n.locale === "en") {
        // 分割文本成两行
        const words = successMsg.split(" ");
        // 使用换行符分隔单词
        const formattedMsg = `${words[0]}\n${words[1] || ""}`;

        Toast.success({
          message: formattedMsg,
          className: "toast-wrap"
        });
      } else {
        // 非英文直接显示
        Toast.success({
          message: successMsg,
          className: "toast-wrap"
        });
      }

      // 返回列表视图
      this.showAchPayment = false;
    },

    /**
     * 处理支付提交事件
     */
    handlePaymentSubmit() {
      // 处理支付提交前的逻辑
    },

    /**
     * 处理支付数据变化事件
     */
    handlePaymentChange(state) {
      // 只更新表单状态和支付数据，不修改userAgreed
      this.isFormValid = state.isValid || false;

      // 只有当有支付方法数据时才更新extendParam
      if (state.data && state.data.paymentMethod) {
        this.extendParam = state.data.paymentMethod;
      }

      // 将状态传递给父组件，但不影响协议勾选状态
      this.$emit("payment-change", {
        isValid: this.isFormValid,
        data: this.extendParam
      });
    },

    // 返回卡列表
    goBack() {
      // 展示卡列表，隐藏支付组件
      this.showAchPayment = false;
      // 选中第一个
      if (this.cardList.length > 0) {
        this.selectedCardId = this.cardList[0].bankAccountToken;
        this.bankAccountToken = this.cardList[0].bankAccountToken;
      }
    }
  },
  created() {
    // 在created阶段缓存URL参数，减少后续查询
    this.cacheUrlParams();

    // 从URL参数中获取标题，如果有的话
    const pageTitle = this.$route.query.title || "ACH";
    if (pageTitle) {
      this.$setPageTitle(pageTitle);
    }
  },
  mounted() {
    this.environment = this.$route.query.environment || "test";
    this.clientKey = this.clientKey || this.$route.query.clientKey || "";
    this.$i18n.locale = this.$route.query.language || "en";

    // 设置页面标题，优先使用路由meta中的title
    if (this.$route.meta && this.$route.meta.title) {
      this.$setPageTitle(this.$route.meta.title);
    } else if (this.$route.meta && this.$route.meta.titleKey) {
      this.$setPageTitle(this.$t(this.$route.meta.titleKey));
    }

    // 获取银行卡列表
    if (this.cachedParams.bankAccountListToken) {
      this.fetchCardList();
    } else {
      // 没有bankAccountListToken时，直接设置数据已加载并展示Adyen组件
      this.dataLoaded = true;
      this.showAchPayment = true;
      this.selectedCardId = "new-card"; // 设置为新卡支付模式
      this.achPaymentComponentLoaded = true;
    }
  }
};
</script>

<style lang="less">
.ach-card-wrap {
  padding: 16px;
  padding-bottom: 90px; // 为底部按钮留出空间
  position: relative;
  min-height: 100vh;
  box-sizing: border-box;

  /* 加载中样式 */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;

    .loading-text {
      margin-top: 15px;
      color: #666;
      font-size: 0.28rem;
    }
  }

  /* 新增 - 右上角返回按钮样式 */
  .backButton {
    position: absolute;
    top: 0.2rem;
    right: 0.3rem;
    display: flex;
    align-items: center;
    z-index: 100;
    cursor: pointer;

    .backIcon {
      width: 0.6rem;
      height: 0.6rem;
      display: flex;
      justify-content: center;
      align-items: center;
      color: white;
      font-weight: bold;
      font-size: 0.24rem;
    }

    .backText {
      font-size: 0.24rem;
      margin-left: 0.1rem;
      color: #666;
    }
  }

  /* 复用 index.vue 的支付按钮样式 */
  .payBtnBox {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 0.16rem 0.4rem;
    background: #fff;
    box-shadow: 0px -6px 10px 0px rgba(0, 0, 0, 0.08);
    z-index: 10;

    .payBtn {
      width: 100%;
      height: 0.88rem;
      text-align: center;
      line-height: 0.88rem;
      font-size: 0.3rem;
      font-weight: 400;
      color: rgba(255, 255, 255, 1);
      background: #ffaa0a;
      border-radius: 0.44rem;
      cursor: pointer;

      &.disabled {
        background: #ccc;
        cursor: not-allowed;
      }

      span {
        margin-left: 0.08rem;
      }
    }
  }

  .delete-message {
    padding: 16px;
    text-align: center;
  }

  .agreement-group {
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
    display: flex;
    cursor: pointer;
    align-items: center;
    justify-content: center;
    font-size: 0.28rem;
    color: #666;

    .agreement-text {
      display: flex;
      align-items: center;
      white-space: nowrap;
      margin-left: 0.1rem;
    }
  }
}

.van-checkbox__icon--checked .van-icon {
  border-radius: 50%;
  background-color: #ffaa0a;
  border-color: #ffaa0a;
}

.van-checkbox__icon .van-icon {
  border-radius: 50%;
}

/* 自定义 Toast 样式 - 移动端宽度自适应 */
.van-toast {
  width: auto !important;
  max-width: 80vw !important;
  min-width: 2rem !important;
  padding: 0.24rem 0.4rem !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  word-break: break-all !important;
  text-align: center !important;
  border-radius: 0.16rem !important;
  font-size: 0.28rem !important;
  line-height: 1.5 !important;
  box-shadow: 0 0.08rem 0.24rem rgba(0, 0, 0, 0.15) !important;
}

/* 针对不同类型的 Toast 调整样式 */
.custom-toast-success {
  .van-toast {
    background-color: #52c41a !important;
    color: #ffffff !important;
  }
}

.custom-toast-error {
  .van-toast {
    background-color: #ff4d4f !important;
    color: #ffffff !important;
  }
}

.custom-toast-warning {
  .van-toast {
    background-color: #faad14 !important;
    color: #ffffff !important;
  }
}

.custom-toast-loading {
  .van-toast {
    background-color: rgba(0, 0, 0, 0.7) !important;
    color: #ffffff !important;
  }
}

/* 超小屏幕适配 */
@media screen and (max-width: 375px) {
  .van-toast {
    max-width: 90vw !important;
    padding: 0.2rem 0.32rem !important;
    font-size: 0.26rem !important;
  }
}
</style>
